// Walidacja konstrukcji. Zwraca liste bledow z kodem i komunikatem PL.
// Blad blokuje start proby i pokazuje konkretny powod.

import { getDefinition, hasDefinition } from './catalog';
import { blueprintCost } from './costs';
import { collectShafts, resolveDrivetrain } from './shafts';
import {
  gridShapesOverlap,
  placedGridShapes,
  shapeCentersWorldGrid,
  socketNormalWorld,
  socketWorldGrid,
  type PlacedGridShape,
} from './geometry';
import { limitBreaches, type PartLimits } from './limits';
import { connectionKey } from './blueprint';
import type { PartInstance, RobotBlueprint } from './types';

export const LIMITS = {
  maxParts: 20,
  // Minimum to jedno kolo - z jednym kolcem proba tez rusza (i zwykle sie
  // przewraca), co jest zamierzone i zabawne.
  minWheels: 1,
  maxWheels: 4,
  batteryCount: 1,
  minDrives: 1,
  maxDrives: 2,
  maxGearsPerDrive: 1,
} as const;

export type ValidationCode =
  | 'UNKNOWN_TYPE'
  | 'DUPLICATE_ID'
  | 'BAD_ROTATION'
  | 'NON_FINITE'
  | 'MISSING_SOCKET'
  | 'SOCKET_OCCUPIED'
  | 'INCOMPATIBLE_SOCKETS'
  | 'GEOMETRY_MISMATCH'
  | 'DISCONNECTED_BODY'
  | 'WHEEL_LINK_COUNT'
  | 'OVERLAP'
  | 'LIMIT_EXCEEDED'
  | 'WHEEL_COUNT'
  | 'MISSING_BATTERY'
  | 'MISSING_DRIVE'
  | 'DRIVE_NO_WHEELS'
  | 'DRIVE_ASSIGNMENT'
  | 'GEAR_ATTACHMENT'
  | 'NO_DRIVEN_WHEEL'
  | 'OVER_BUDGET'
  | 'PART_LIMIT'
  | 'NO_PART';

export interface ValidationIssue {
  code: ValidationCode;
  message: string;
  partIds: string[];
}

const POSITION_EPS = 1e-6;
const OVERLAP_EPS = 1e-4;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function validateBlueprint(blueprint: RobotBlueprint): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const push = (code: ValidationCode, message: string, partIds: string[] = []) =>
    issues.push({ code, message, partIds });

  if (blueprint.parts.length === 0) {
    push('NO_PART', 'Konstrukcja nie ma zadnych czesci.');
    return issues;
  }

  // 1-4: identyfikatory, typy, obroty, liczby skonczone.
  const seen = new Set<string>();
  for (const part of blueprint.parts) {
    if (seen.has(part.id)) push('DUPLICATE_ID', `Zduplikowane id czesci: ${part.id}`, [part.id]);
    seen.add(part.id);

    if (!hasDefinition(part.typeId)) {
      push('UNKNOWN_TYPE', `Nieznany typ czesci: ${part.typeId}`, [part.id]);
    }
    if (![0, 90, 180, 270].includes(part.rotation)) {
      push('BAD_ROTATION', `Niedozwolony obrot w czesci ${part.id}`, [part.id]);
    }
    if (!isFiniteNumber(part.gridX) || !isFiniteNumber(part.gridY)) {
      push('NON_FINITE', `Nieprawidlowa pozycja czesci ${part.id}`, [part.id]);
    }
  }
  if (issues.length > 0) return issues;

  const byId = new Map(blueprint.parts.map((part) => [part.id, part] as const));

  // 5-8: sockets, zajetosc mocowan, zgodnosc typow i geometria polaczen.
  const used = new Map<string, number>();
  for (const conn of blueprint.connections) {
    const partA = byId.get(conn.a.partId);
    const partB = byId.get(conn.b.partId);
    if (!partA || !partB) {
      push('MISSING_SOCKET', 'Polaczenie wskazuje nieistniejaca czesc', [
        conn.a.partId,
        conn.b.partId,
      ]);
      continue;
    }
    const defA = getDefinition(partA.typeId);
    const defB = getDefinition(partB.typeId);
    const sockA = defA.sockets.find((s) => s.id === conn.a.socketId);
    const sockB = defB.sockets.find((s) => s.id === conn.b.socketId);
    if (!sockA || !sockB) {
      push('MISSING_SOCKET', 'Polaczenie uzywa nieistniejacego socketu', [
        conn.a.partId,
        conn.b.partId,
      ]);
      continue;
    }

    // Os ('axle') moze nosic wiele czesci wspolosiowych (np. zebatka + kolo) -
    // tak dziala prawdziwy wal. Tylko mocowania 'rigid' moga byc zajete raz.
    if (sockA.kind === 'rigid') {
      used.set(connectionKey(conn.a), (used.get(connectionKey(conn.a)) ?? 0) + 1);
    }
    if (sockB.kind === 'rigid') {
      used.set(connectionKey(conn.b), (used.get(connectionKey(conn.b)) ?? 0) + 1);
    }

    if (sockA.kind !== sockB.kind) {
      push('INCOMPATIBLE_SOCKETS', 'Mozna laczyc tylko rigid z rigid albo axle z axle', [
        conn.a.partId,
        conn.b.partId,
      ]);
      continue;
    }

    const posA = socketWorldGrid(partA, defA, conn.a.socketId);
    const posB = socketWorldGrid(partB, defB, conn.b.socketId);
    if (Math.abs(posA.x - posB.x) > POSITION_EPS || Math.abs(posA.y - posB.y) > POSITION_EPS) {
      push('GEOMETRY_MISMATCH', 'Sockety nie pokrywaja sie po obrocie', [
        conn.a.partId,
        conn.b.partId,
      ]);
      continue;
    }
    if (sockA.kind === 'rigid') {
      const nA = socketNormalWorld(defA, conn.a.socketId, partA.rotation);
      const nB = socketNormalWorld(defB, conn.b.socketId, partB.rotation);
      if (
        !nA ||
        !nB ||
        Math.abs(nA.x + nB.x) > POSITION_EPS ||
        Math.abs(nA.y + nB.y) > POSITION_EPS
      ) {
        push('GEOMETRY_MISMATCH', 'Normalne sztywnych mocowan nie sa przeciwne', [
          conn.a.partId,
          conn.b.partId,
        ]);
      }
    }
  }

  for (const [key, count] of used) {
    if (count > 1) {
      push('SOCKET_OCCUPIED', `Mocowanie zajete wiecej niz raz: ${key}`, [key.split('#')[0] ?? '']);
    }
  }

  return issues;
}

/** Reguly wymagajace poprawnej topologii - wolane po walidacji podstawowej. */
export function validateTopology(blueprint: RobotBlueprint): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const push = (code: ValidationCode, message: string, partIds: string[] = []) =>
    issues.push({ code, message, partIds });

  const parts = blueprint.parts;
  const byId = new Map(parts.map((part) => [part.id, part] as const));

  const wheels: PartInstance[] = [];
  const chassis: PartInstance[] = [];
  let batteryCount = 0;
  let driveCount = 0;

  for (const part of parts) {
    const def = getDefinition(part.typeId);
    if (def.category === 'wheel') wheels.push(part);
    else chassis.push(part);
    if (def.category === 'battery') batteryCount += 1;
    if (def.category === 'drive') driveCount += 1;
  }

  if (parts.length > LIMITS.maxParts) {
    push('LIMIT_EXCEEDED', `Za duzo czesci (limit ${LIMITS.maxParts}).`);
  }
  if (wheels.length < LIMITS.minWheels || wheels.length > LIMITS.maxWheels) {
    push('WHEEL_COUNT', `Liczba kol musi byc w zakresie ${LIMITS.minWheels}-${LIMITS.maxWheels}.`);
  }
  if (batteryCount !== LIMITS.batteryCount) {
    push('MISSING_BATTERY', 'Konstrukcja wymaga dokladnie jednego akumulatora.');
  }
  if (driveCount < LIMITS.minDrives || driveCount > LIMITS.maxDrives) {
    push(
      'MISSING_DRIVE',
      `Liczba modulow napedu musi byc w zakresie ${LIMITS.minDrives}-${LIMITS.maxDrives}.`,
    );
  }

  // Naped plynie przez POLACZENIA, nie przez jawne przypisanie: kolo jest
  // napedzane tylko wtedy, gdy jego wal jest osiagalny od modulu napedu (przez
  // waly, zazebienia zebat i lancuchy). Ten sam mechanizm obsluguje oba tryby:
  // kolo WPROST na wale silnika (przelozenie 1) i kolo za przekladniami.
  const wheelIdSet = new Set(wheels.map((wheel) => wheel.id));
  const drivetrain = resolveDrivetrain(blueprint);
  const poweredShafts = new Set(drivetrain.ratios.keys());
  const shaftOfWheel = new Map<string, string>();
  for (const shaft of drivetrain.shafts) {
    for (const memberId of shaft.memberPartIds) {
      if (wheelIdSet.has(memberId)) shaftOfWheel.set(memberId, shaft.id);
    }
  }
  const drivenWheelCount = wheels.filter((wheel) => {
    const shaftId = shaftOfWheel.get(wheel.id);
    return shaftId !== undefined && poweredShafts.has(shaftId);
  }).length;

  for (const wheel of wheels) {
    if (!shaftOfWheel.has(wheel.id)) {
      push('NO_DRIVEN_WHEEL', `Kolo ${wheel.id} nie jest osadzone na zadnym wale.`, [wheel.id]);
    }
  }
  if (drivenWheelCount === 0 && wheels.length > 0) {
    push(
      'NO_DRIVEN_WHEEL',
      'Zaden wal z kolem nie ma drogi do modulu napedu - osadz kolo na wale silnika albo doprowadz naped przez przekladnie.',
      [],
    );
  }

  // Spojnosc korpusu: czesci niebedace kolami tworza jeden sztywny korpus.
  const adjacency = new Map<string, Set<string>>();
  for (const part of parts) adjacency.set(part.id, new Set());
  for (const conn of blueprint.connections) {
    adjacency.get(conn.a.partId)?.add(conn.b.partId);
    adjacency.get(conn.b.partId)?.add(conn.a.partId);
  }

  const firstChassis = chassis[0];
  if (firstChassis) {
    const visited = new Set<string>();
    const stack = [firstChassis.id];
    while (stack.length > 0) {
      const current = stack.pop();
      if (current === undefined || visited.has(current)) continue;
      visited.add(current);
      for (const next of adjacency.get(current) ?? []) {
        const nextPart = byId.get(next);
        if (!nextPart) continue;
        if (getDefinition(nextPart.typeId).category === 'wheel') continue;
        if (!visited.has(next)) stack.push(next);
      }
    }
    const missing = chassis.filter((part) => !visited.has(part.id));
    if (missing.length > 0) {
      push(
        'DISCONNECTED_BODY',
        `Czesci niepolaczone z korpusem: ${missing.map((p) => p.id).join(', ')}`,
        missing.map((p) => p.id),
      );
    }
  }

  // Kazde kolo: dokladnie jedno polaczenie axle z korpusem.
  for (const wheel of wheels) {
    const links = blueprint.connections.filter(
      (conn) => conn.a.partId === wheel.id || conn.b.partId === wheel.id,
    );
    if (links.length !== 1) {
      push('WHEEL_LINK_COUNT', `Kolo ${wheel.id} musi miec dokladnie jedno polaczenie axle.`, [
        wheel.id,
      ]);
      continue;
    }
    const link = links[0];
    if (!link) continue;
    const otherId = link.a.partId === wheel.id ? link.b.partId : link.a.partId;
    const other = byId.get(otherId);
    if (!other || getDefinition(other.typeId).category === 'wheel') {
      push('WHEEL_LINK_COUNT', `Kolo ${wheel.id} musi byc polaczone z korpusem, nie z innym kolem.`, [
        wheel.id,
      ]);
    }
  }

  // Nakladanie: wnetrza klockow korpusu nie moga sie przecinac. Sprawdzamy
  // wszystkie ksztalty (prostokaty i okregi), a nie tylko prostokaty.
  // Stykanie krawedzi jest dozwolone - dopuszczamy tylko wspolna czesc objetosci.
  //
  // WYJATEK: czesci wspolosiowe (osadzone na JEDNYM wale) moga na siebie
  // nachodzic - inaczej nie da sie zbudowac przekladni zlozonej (dwie zebatki
  // na jednej osi), a to podstawowy sposob zmiany przelozenia.
  const coaxialShafts = collectShafts(blueprint);
  // Czesci na tym samym wale to takze SAM WSPORNIK (holder) i jego czlonkowie -
  // inaczej zebatka albo kolo osadzone na wsporniku osi byloby zglaszane jako
  // nakladanie z korpusem wspornika.
  const onShaft = (shaft: (typeof coaxialShafts)[number], partId: string): boolean =>
    shaft.holders.some((holder) => holder.partId === partId) || shaft.memberPartIds.includes(partId);
  const coaxial = (leftId: string, rightId: string): boolean =>
    coaxialShafts.some((shaft) => onShaft(shaft, leftId) && onShaft(shaft, rightId));

  const chassisShapes: { id: string; shape: PlacedGridShape }[] = [];
  for (const part of chassis) {
    for (const shape of placedGridShapes(part, getDefinition(part.typeId))) {
      chassisShapes.push({ id: part.id, shape });
    }
  }
  for (let i = 0; i < chassisShapes.length; i += 1) {
    for (let j = i + 1; j < chassisShapes.length; j += 1) {
      const a = chassisShapes[i];
      const b = chassisShapes[j];
      if (!a || !b) continue;
      // Ksztalty tej samej czesci oraz czesci wspolosiowych moga sie nakladac.
      if (a.id === b.id || coaxial(a.id, b.id)) continue;
      if (gridShapesOverlap(a.shape, b.shape, OVERLAP_EPS)) {
        push('OVERLAP', `Czesci nachodza na siebie: ${a.id} i ${b.id}`, [a.id, b.id]);
      }
    }
  }

  for (let i = 0; i < wheels.length; i += 1) {
    for (let j = i + 1; j < wheels.length; j += 1) {
      const a = wheels[i];
      const b = wheels[j];
      if (!a || !b) continue;
      const defA = getDefinition(a.typeId);
      const defB = getDefinition(b.typeId);
      const cA = shapeCentersWorldGrid(a, defA)[0];
      const cB = shapeCentersWorldGrid(b, defB)[0];
      if (!cA || !cB) continue;
      const rA = defA.wheel?.radiusGrid ?? 0;
      const rB = defB.wheel?.radiusGrid ?? 0;
      const dist = Math.hypot(cA.center.x - cB.center.x, cA.center.y - cB.center.y);
      if (dist < rA + rB - OVERLAP_EPS) {
        push('OVERLAP', `Kola nachodza na siebie: ${a.id} i ${b.id}`, [a.id, b.id]);
      }
    }
  }

  return issues;
}

export function validateAll(
  blueprint: RobotBlueprint,
  budgetPln?: number,
  limits?: PartLimits,
): ValidationIssue[] {
  return [
    ...validateBlueprint(blueprint),
    ...validateTopology(blueprint),
    ...validateBudget(blueprint, budgetPln),
    ...validateLimits(blueprint, limits),
  ];
}

/**
 * Limit kosztu konstrukcji (osobna os od gotowosci):
 *  - sprawdzany tylko przy pelnej walidacji (blokada Testu),
 *  - NIE trafia do validateBuilding, wiec mozna zbudowac ponad budzet i potem
 *    zdjac klocki - inaczej nie daloby sie dokladac czesci, by cokolwiek zmienic.
 */
function validateBudget(
  blueprint: RobotBlueprint,
  budgetPln: number | undefined,
): ValidationIssue[] {
  if (budgetPln === undefined || !Number.isFinite(budgetPln)) return [];
  const cost = blueprintCost(blueprint);
  if (cost > budgetPln) {
    return [
      {
        code: 'OVER_BUDGET',
        message: `Koszt konstrukcji ${cost} przekracza budzet ${budgetPln}.`,
        partIds: [],
      },
    ];
  }
  return [];
}

/**
 * Limity klockow per trasa (osobna os od budzetu i gotowosci): mapa moze
 * ograniczac, ile czego wolno zbudowac (np. najwyzej dwa kola). Kod PART_LIMIT
 * blokuje Test jako siatka bezpieczenstwa - na co dzien dokladanie pilnuje
 * magazyn konstrukcji (BlueprintStore.addPart).
 */
function validateLimits(
  blueprint: RobotBlueprint,
  limits: PartLimits | undefined,
): ValidationIssue[] {
  return limitBreaches(blueprint, limits).map((breach) => ({
    code: 'PART_LIMIT' as const,
    message: `Limit tej trasy: najwyzej ${breach.max} x ${breach.label} (jest ${breach.used}).`,
    partIds: breach.partIds,
  }));
}

/**
 * Kody regul opisujacych "konstrukcja jest gotowa do proby".
 * W trakcie montazu nie moga blokowac dokladania klockow - inaczej nie daloby
 * sie zbudowac robota od zera (pierwsza czesc nie ma jeszcze kol, napedu,
 * a swiezo dodany modul napedu nie ma jeszcze przypisanych kol).
 */
const READINESS_CODES: ReadonlySet<ValidationCode> = new Set([
  'WHEEL_COUNT',
  'MISSING_BATTERY',
  'MISSING_DRIVE',
  'NO_DRIVEN_WHEEL',
]);

/**
 * Czy kod dotyczy tylko GOTOWOSCI (kompletnosci) konstrukcji, a nie jej struktury.
 * Braki gotowosci nie uniemozliwiaja symulacji - taka konstrukcja najwyzej nie
 * ruszy z miejsca, wiec mozna ja wypuscic na trase.
 */
export function isReadinessCode(code: ValidationCode): boolean {
  return READINESS_CODES.has(code);
}

/**
 * Walidacja w trakcie budowania: pelna walidacja podstawowa oraz struktura
 * (spojnosc korpusu, mocowania kol, nakladanie), ale bez regul gotowosci.
 * Uzywana do oceny kandydatury montazu i do blokady Testu z konkretnym powodem.
 */
export function validateBuilding(blueprint: RobotBlueprint): ValidationIssue[] {
  return [
    ...validateBlueprint(blueprint),
    ...validateTopology(blueprint).filter((issue) => !READINESS_CODES.has(issue.code)),
  ];
}

/**
 * Walidacja dopuszczajaca WYJSCIE NA TRASE z niegotowa konstrukcja (np. bez
 * akumulatora, napedu albo kol). Blokuja tylko bledy STRUKTURALNE (ktore
 * uniemozliwiaja sensowna symulacje) oraz przekroczenie budzetu - brak
 * gotowosci nie przeszkadza, bo taka konstrukcja co najwyzej nie pojedzie.
 * Uzywana przez przycisk "Testuj na trase".
 */
export function validateForTrial(
  blueprint: RobotBlueprint,
  budgetPln?: number,
  limits?: PartLimits,
): ValidationIssue[] {
  return [
    ...validateBuilding(blueprint),
    ...validateBudget(blueprint, budgetPln),
    ...validateLimits(blueprint, limits),
  ];
}

